import { useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  downloadLocalBackup,
  restoreFromFile,
  fetchHetznerStatus,
  testHetznerConnection,
  listHetznerBackups,
  uploadBackupToHetzner,
  restoreFromHetzner,
  deleteHetznerBackup,
  formatBytes,
  fetchBackupSchedule,
  updateBackupSchedule,
  triggerBackupNow,
} from '@/api/backup.api';
import type { RemoteBackupFile, RestoreReport, BackupScheduleConfig } from '@genealogy-app/shared';
import { BACKUP_TABLE_LABELS } from '@genealogy-app/shared';
import { TreePine } from 'lucide-react';
import { listTrees } from '@/api/trees.api';
import { TreeBackupPanel } from '@/components/Backup/TreeBackupPanel';
import { TreeFromGlobalBackup } from '@/components/Backup/TreeFromGlobalBackup';
import { TreeFromRemoteBackup } from '@/components/Backup/TreeFromRemoteBackup';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ─── Restore-Bestätigungsdialog ─────────────────────────────────────────────

interface RestoreConfirmModalProps {
  title: string;
  message: string;
  confirmLabel?: string;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
  busy?: boolean;
}

function ConfirmModal({
  title,
  message,
  confirmLabel = 'Wiederherstellen',
  onConfirm,
  onClose,
  busy,
}: RestoreConfirmModalProps) {
  const CONFIRM_WORD = 'WIEDERHERSTELLEN';
  const [input, setInput] = useState('');

  return (
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/50 p-4"
      onClick={busy ? undefined : onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-amber-200 bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
            <svg
              className="h-5 w-5 text-amber-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
            <p className="text-sm text-slate-500">Diese Aktion ersetzt alle Daten.</p>
          </div>
        </div>

        <div className="mb-4 rounded-md bg-amber-50 p-3">
          <p className="text-sm text-amber-800">{message}</p>
        </div>

        <div className="mb-4">
          <label htmlFor="confirmRestore" className="mb-1 block text-sm font-medium text-slate-700">
            Tippe <span className="font-mono font-bold text-amber-700">{CONFIRM_WORD}</span> zur
            Bestätigung
          </label>
          <input
            id="confirmRestore"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            autoFocus
            autoComplete="off"
            disabled={busy}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100 disabled:opacity-50"
            placeholder={CONFIRM_WORD}
          />
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            onClick={() => void onConfirm()}
            disabled={input !== CONFIRM_WORD || busy}
            className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {busy ? 'Wird wiederhergestellt…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Report-Anzeige nach Restore ────────────────────────────────────────────

function RestoreReportCard({ report }: { report: RestoreReport }) {
  // Nur Zeilen mit Inhalt zeigen – eine Liste aus fünfzehn Nullen sagt nichts.
  const rows = Object.keys(BACKUP_TABLE_LABELS).filter((key) => (report.restored[key] ?? 0) > 0);

  return (
    <div className="rounded-md border border-green-200 bg-green-50 p-4 text-sm">
      <p className="mb-2 font-semibold text-green-800">✓ Backup wiederhergestellt</p>
      <ul className="grid grid-cols-1 gap-x-4 gap-y-1 text-slate-700 sm:grid-cols-2">
        {rows.map((key) => (
          <li key={key}>
            {BACKUP_TABLE_LABELS[key]}: <strong>{report.restored[key]}</strong>
          </li>
        ))}
        {report.trees > 0 && (
          <li>
            Stammbäume: <strong>{report.trees}</strong>
          </li>
        )}
        {report.treeMemberships > 0 && (
          <li>
            Berechtigungen: <strong>{report.treeMemberships}</strong>
          </li>
        )}
        <li>
          Dateien im Archiv: <strong>{report.mediaFilesRestored}</strong>
        </li>
      </ul>
      {report.mediaFilesMissing > 0 && (
        <p className="mt-2 text-xs text-amber-700">
          ⚠ {report.mediaFilesMissing} Dateien fehlen im Archiv.
        </p>
      )}
      {report.notes.length > 0 && (
        <ul className="mt-3 space-y-1 border-t border-green-200 pt-2 text-xs text-amber-700">
          {report.notes.map((note) => (
            <li key={note}>⚠ {note}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Lokaler Bereich ────────────────────────────────────────────────────────

function LocalBackupSection() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [lastReport, setLastReport] = useState<RestoreReport | null>(null);
  const [downloading, setDownloading] = useState(false);

  const restoreMutation = useMutation({
    mutationFn: (file: File) => restoreFromFile(file),
    onSuccess: (data) => {
      setLastReport(data.report);
      setPendingFile(null);
      toast.success('Backup wiederhergestellt.');
      queryClient.invalidateQueries();
    },
    onError: (err: Error) => {
      toast.error(`Wiederherstellung fehlgeschlagen: ${err.message}`);
    },
  });

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadLocalBackup();
      toast.success('Backup-Download gestartet.');
    } catch (err) {
      toast.error(`Download fehlgeschlagen: ${(err as Error).message}`);
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setPendingFile(file);
    // Input resetten, damit dieselbe Datei erneut ausgewählt werden kann
    e.target.value = '';
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <div className="mb-4 flex items-center gap-2">
        <span className="text-xl">💾</span>
        <h2 className="text-lg font-semibold text-slate-900">Lokales Backup</h2>
      </div>
      <p className="mb-4 text-sm text-slate-600">
        Lade ein komplettes Backup als ZIP-Datei herunter oder stelle einen zuvor heruntergeladenen
        Stand wieder her.
      </p>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
          </svg>
          {downloading ? 'Wird vorbereitet…' : 'Backup herunterladen'}
        </button>

        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={restoreMutation.isPending}
          className="flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
          </svg>
          Backup hochladen & wiederherstellen
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept=".zip,application/zip"
          onChange={handleFileSelected}
          className="hidden"
        />
      </div>

      {lastReport && (
        <div className="mt-4">
          <RestoreReportCard report={lastReport} />
        </div>
      )}

      {pendingFile && (
        <ConfirmModal
          title="Backup wiederherstellen?"
          message={`Die Datei "${pendingFile.name}" ersetzt alle vorhandenen Personen, Beziehungen, Medien und Quellen. Benutzerkonten bleiben erhalten. Diese Aktion kann nicht rückgängig gemacht werden.`}
          busy={restoreMutation.isPending}
          onClose={() => setPendingFile(null)}
          onConfirm={async () => {
            await restoreMutation.mutateAsync(pendingFile);
          }}
        />
      )}
    </section>
  );
}

// ─── Hetzner-Bereich ────────────────────────────────────────────────────────

function HetznerSection() {
  const queryClient = useQueryClient();
  const [restoreTarget, setRestoreTarget] = useState<RemoteBackupFile | null>(null);
  /** Dateiname des Backups, aus dem gerade ein einzelner Baum geholt wird. */
  const [treeTarget, setTreeTarget] = useState<string | null>(null);
  const [lastReport, setLastReport] = useState<RestoreReport | null>(null);

  const statusQuery = useQuery({
    queryKey: ['backup', 'hetzner', 'status'],
    queryFn: fetchHetznerStatus,
  });

  const listQuery = useQuery({
    queryKey: ['backup', 'hetzner', 'list'],
    queryFn: listHetznerBackups,
    enabled: statusQuery.data?.configured === true,
    staleTime: 30_000,
  });

  const testMutation = useMutation({
    mutationFn: testHetznerConnection,
    onSuccess: () => toast.success('Verbindung zur Hetzner Storage Box erfolgreich.'),
    onError: (err: Error) => toast.error(`Verbindungstest fehlgeschlagen: ${err.message}`),
  });

  const uploadMutation = useMutation({
    mutationFn: uploadBackupToHetzner,
    onSuccess: (data) => {
      toast.success(`Backup auf Storage Box gesichert: ${data.filename}`);
      queryClient.invalidateQueries({ queryKey: ['backup', 'hetzner', 'list'] });
    },
    onError: (err: Error) => toast.error(`Upload fehlgeschlagen: ${err.message}`),
  });

  const restoreMutation = useMutation({
    mutationFn: (filename: string) => restoreFromHetzner(filename),
    onSuccess: (data) => {
      setLastReport(data.report);
      setRestoreTarget(null);
      toast.success('Backup wiederhergestellt.');
      queryClient.invalidateQueries();
    },
    onError: (err: Error) => toast.error(`Wiederherstellung fehlgeschlagen: ${err.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: (filename: string) => deleteHetznerBackup(filename),
    onSuccess: (_, filename) => {
      toast.success(`Gelöscht: ${filename}`);
      queryClient.invalidateQueries({ queryKey: ['backup', 'hetzner', 'list'] });
    },
    onError: (err: Error) => toast.error(`Löschen fehlgeschlagen: ${err.message}`),
  });

  const status = statusQuery.data;

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <div className="mb-4 flex items-center gap-2">
        <span className="text-xl">🗄️</span>
        <h2 className="text-lg font-semibold text-slate-900">Hetzner Storage Box</h2>
      </div>

      {statusQuery.isLoading && (
        <p className="text-sm text-slate-500">Status wird geladen…</p>
      )}

      {status && !status.configured && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <p className="mb-2 font-semibold">Noch nicht konfiguriert</p>
          <p className="mb-2">
            Setze die folgenden Umgebungsvariablen in deiner <code className="rounded bg-amber-100 px-1 py-0.5 text-xs">.env</code>, um die Hetzner Storage Box zu nutzen:
          </p>
          <pre className="overflow-x-auto rounded bg-amber-100 p-2 text-xs text-amber-900">
{`HETZNER_SBOX_HOST=u123456.your-storagebox.de
HETZNER_SBOX_PORT=23
HETZNER_SBOX_USER=u123456
HETZNER_SBOX_PASSWORD=dein-passwort
# oder alternativ:
# HETZNER_SBOX_KEY_PATH=/pfad/zu/id_rsa
HETZNER_SBOX_PATH=/genealogy-backups`}
          </pre>
          <p className="mt-2 text-xs">
            Nach dem Ändern der <code>.env</code> muss der Backend-Server neu gestartet werden.
          </p>
        </div>
      )}

      {status?.configured && (
        <>
          <div className="mb-4 rounded-md bg-slate-50 p-3 text-sm">
            <div className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1">
              <span className="text-slate-500">Host:</span>
              <span className="font-mono text-slate-800">{status.host}</span>
              <span className="text-slate-500">Benutzer:</span>
              <span className="font-mono text-slate-800">{status.username}</span>
              <span className="text-slate-500">Verzeichnis:</span>
              <span className="font-mono text-slate-800">{status.remotePath}</span>
              <span className="text-slate-500">Authentifizierung:</span>
              <span className="text-slate-800">
                {status.authMethod === 'key' ? 'SSH-Key' : 'Passwort'}
              </span>
            </div>
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            <button
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending}
              className="flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {testMutation.isPending ? 'Teste…' : 'Verbindung testen'}
            </button>

            <button
              onClick={() => uploadMutation.mutate()}
              disabled={uploadMutation.isPending}
              className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
              </svg>
              {uploadMutation.isPending ? 'Sichere…' : 'Jetzt auf Storage Box sichern'}
            </button>

            <button
              onClick={() => listQuery.refetch()}
              disabled={listQuery.isFetching}
              className="flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {listQuery.isFetching ? 'Lade…' : 'Liste aktualisieren'}
            </button>
          </div>

          {/* Remote-Liste */}
          <div className="rounded-md border border-slate-200">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Backups auf der Storage Box
            </div>
            {listQuery.isLoading && (
              <div className="p-4 text-sm text-slate-500">Wird geladen…</div>
            )}
            {listQuery.isError && (
              <div className="p-4 text-sm text-red-600">
                Fehler beim Laden: {(listQuery.error as Error).message}
              </div>
            )}
            {listQuery.data && listQuery.data.length === 0 && (
              <div className="p-4 text-sm text-slate-500">
                Noch keine Backups vorhanden.
              </div>
            )}
            {/* Liste statt Tabelle: eine Tabelle mit vier Spalten und drei
                Knöpfen erzwingt auf dem Handy waagerechtes Scrollen – und weil
                sie im Seiten-Container liegt, verschiebt sich dabei die ganze
                Seite. Als Liste bricht jede Zeile einfach um. */}
            {listQuery.data && listQuery.data.length > 0 && (
              <ul>
                {listQuery.data.map((backup) => (
                  <li key={backup.name} className="border-b border-slate-100 last:border-0">
                    <div className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-4">
                      <div className="min-w-0">
                        {/* break-all: der Dateiname ist eine lange Zeichenkette
                            ohne Trennstellen und würde sonst überstehen. */}
                        <p className="break-all font-mono text-xs text-slate-700">{backup.name}</p>
                        <p className="mt-0.5 text-xs text-slate-500">
                          {formatBytes(backup.size)} · {formatDate(backup.modifiedAt)}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2 sm:shrink-0 sm:justify-end">
                        <button
                          onClick={() =>
                            setTreeTarget(treeTarget === backup.name ? null : backup.name)
                          }
                          disabled={restoreMutation.isPending || deleteMutation.isPending}
                          className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        >
                          {treeTarget === backup.name ? 'Schließen' : 'Einzelnen Stammbaum'}
                        </button>
                        <button
                          onClick={() => setRestoreTarget(backup)}
                          disabled={restoreMutation.isPending || deleteMutation.isPending}
                          className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                        >
                          Alles wiederherstellen
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(`"${backup.name}" dauerhaft von der Storage Box löschen?`)) {
                              deleteMutation.mutate(backup.name);
                            }
                          }}
                          disabled={deleteMutation.isPending}
                          className="rounded border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                        >
                          Löschen
                        </button>
                      </div>
                    </div>

                    {/* Die Auswahl gehört unter die Zeile, auf die geklickt
                        wurde – nicht ans Ende der Liste. */}
                    {treeTarget === backup.name && (
                      <div className="p-3 pt-0 sm:px-4 sm:pb-4">
                        <TreeFromRemoteBackup
                          filename={backup.name}
                          onDone={() => setTreeTarget(null)}
                        />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {lastReport && (
            <div className="mt-4">
              <RestoreReportCard report={lastReport} />
            </div>
          )}
        </>
      )}

      {restoreTarget && (
        <ConfirmModal
          title="Backup wiederherstellen?"
          message={`"${restoreTarget.name}" (${formatBytes(restoreTarget.size)}) ersetzt alle vorhandenen Personen, Beziehungen, Medien und Quellen. Benutzerkonten bleiben erhalten. Diese Aktion kann nicht rückgängig gemacht werden.`}
          busy={restoreMutation.isPending}
          onClose={() => setRestoreTarget(null)}
          onConfirm={async () => {
            await restoreMutation.mutateAsync(restoreTarget.name);
          }}
        />
      )}
    </section>
  );
}

// ─── Auto-Backup ────────────────────────────────────────────────────────────

const DAY_NAMES = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

// ─── Einzelne Stammbäume ────────────────────────────────────────────────────
//
// Zwei Wege, die hier zusammenkommen: die Sicherung eines Baums als eigene
// Datei, und das Herauslösen eines Baums aus einem Gesamt-Backup. Beides ist
// Administratoren vorbehalten – eine Wiederherstellung ersetzt den gesamten
// Inhalt eines Stammbaums.

function TreeSection() {
  const { data: trees, isLoading } = useQuery({ queryKey: ['trees'], queryFn: listTrees });
  const [selectedId, setSelectedId] = useState('');

  // Erster Baum als Vorauswahl, damit die Sektion nicht leer wirkt.
  const treeId = selectedId || trees?.[0]?.id || '';
  const treeName = trees?.find((t) => t.id === treeId)?.name ?? '';

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <div className="mb-4 flex items-center gap-2">
        <TreePine className="h-5 w-5 text-slate-400" aria-hidden="true" />
        <h2 className="text-lg font-semibold text-slate-900">Einzelne Stammbäume</h2>
      </div>
      <p className="mb-4 text-sm text-slate-600">
        Einen einzelnen Stammbaum sichern oder zurückholen, ohne die übrigen anzufassen.
      </p>

      {isLoading && <p className="text-sm text-slate-500">Stammbäume werden geladen…</p>}

      {trees && trees.length > 0 && (
        <>
          <label htmlFor="tree-select" className="block text-xs font-medium text-slate-700">
            Stammbaum
          </label>
          <select
            id="tree-select"
            value={treeId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 sm:max-w-sm"
          >
            {trees.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>

          <div className="mt-4 space-y-4">
            {/* key: bei einem Baumwechsel den Zustand (gewählte Datei, Vorschau)
                verwerfen – eine Vorschau für Baum A darf nicht für B gelten. */}
            <TreeBackupPanel key={treeId} treeId={treeId} treeName={treeName} />
            <TreeFromGlobalBackup />
          </div>
        </>
      )}
    </section>
  );
}

function StatusBadge({ status }: { status: BackupScheduleConfig['lastStatus'] }) {
  if (!status) return null;
  const ok = status === 'success';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
        ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}
    >
      {ok ? '✓ Erfolgreich' : '✗ Fehler'}
    </span>
  );
}

function AutoBackupSection() {
  const queryClient = useQueryClient();

  const scheduleQuery = useQuery({
    queryKey: ['backup', 'schedule'],
    queryFn: fetchBackupSchedule,
    staleTime: 30_000,
  });

  const updateMutation = useMutation({
    mutationFn: updateBackupSchedule,
    onSuccess: (data) => {
      queryClient.setQueryData(['backup', 'schedule'], data);
    },
    onError: (err: Error) => toast.error(`Fehler beim Speichern: ${err.message}`),
  });

  const runNowMutation = useMutation({
    mutationFn: triggerBackupNow,
    onSuccess: (data) => {
      queryClient.setQueryData(['backup', 'schedule'], data.config);
      toast.success('Backup wurde erfolgreich gestartet.');
      queryClient.invalidateQueries({ queryKey: ['backup', 'hetzner', 'list'] });
    },
    onError: (err: Error) => toast.error(`Backup fehlgeschlagen: ${err.message}`),
  });

  const cfg = scheduleQuery.data;

  const handleToggle = () => {
    if (!cfg) return;
    updateMutation.mutate({ enabled: !cfg.enabled });
  };

  const handleField = <K extends keyof Parameters<typeof updateBackupSchedule>[0]>(
    key: K,
    value: Parameters<typeof updateBackupSchedule>[0][K],
  ) => {
    updateMutation.mutate({ [key]: value } as Parameters<typeof updateBackupSchedule>[0]);
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <div className="mb-4 flex items-center gap-2">
        <span className="text-xl">🕐</span>
        <h2 className="text-lg font-semibold text-slate-900">Automatische Backups</h2>
      </div>

      {scheduleQuery.isLoading && (
        <p className="text-sm text-slate-500">Wird geladen…</p>
      )}

      {cfg && (
        <>
          {/* Aktivierungs-Toggle */}
          <div className="mb-5 flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div>
              <p className="font-medium text-slate-900">Automatische Backups aktivieren</p>
              <p className="text-sm text-slate-500">
                Erstellt Backups auf der Hetzner Storage Box nach dem eingestellten Zeitplan.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={cfg.enabled}
              onClick={handleToggle}
              disabled={updateMutation.isPending}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
                cfg.enabled ? 'bg-blue-600' : 'bg-slate-300'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${
                  cfg.enabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Einstellungen – nur sichtbar wenn aktiviert */}
          {cfg.enabled && (
            <div className="mb-5 grid gap-4 sm:grid-cols-2">
              {/* Intervall */}
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Wiederholung
                </label>
                <select
                  value={cfg.interval}
                  onChange={(e) => handleField('interval', e.target.value as BackupScheduleConfig['interval'])}
                  disabled={updateMutation.isPending}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:opacity-50"
                >
                  <option value="daily">Täglich</option>
                  <option value="weekly">Wöchentlich</option>
                  <option value="monthly">Monatlich</option>
                </select>
              </div>

              {/* Uhrzeit */}
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Uhrzeit
                </label>
                <input
                  type="time"
                  value={cfg.time}
                  onChange={(e) => handleField('time', e.target.value)}
                  disabled={updateMutation.isPending}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:opacity-50"
                />
              </div>

              {/* Wochentag (nur weekly) */}
              {cfg.interval === 'weekly' && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Wochentag
                  </label>
                  <select
                    value={cfg.dayOfWeek}
                    onChange={(e) => handleField('dayOfWeek', Number(e.target.value))}
                    disabled={updateMutation.isPending}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:opacity-50"
                  >
                    {DAY_NAMES.map((name, i) => (
                      <option key={i} value={i}>{name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Tag im Monat (nur monthly) */}
              {cfg.interval === 'monthly' && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Tag im Monat
                  </label>
                  <select
                    value={cfg.dayOfMonth}
                    onChange={(e) => handleField('dayOfMonth', Number(e.target.value))}
                    disabled={updateMutation.isPending}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:opacity-50"
                  >
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d}>{d}.</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Anzahl behalten */}
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Backups aufbewahren
                </label>
                <select
                  value={cfg.keepLast}
                  onChange={(e) => handleField('keepLast', Number(e.target.value))}
                  disabled={updateMutation.isPending}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:opacity-50"
                >
                  <option value={0}>Alle (unbegrenzt)</option>
                  <option value={3}>Letzte 3</option>
                  <option value={5}>Letzte 5</option>
                  <option value={7}>Letzte 7</option>
                  <option value={14}>Letzte 14</option>
                  <option value={30}>Letzte 30</option>
                </select>
              </div>
            </div>
          )}

          {/* Status & nächster Lauf */}
          <div className="mb-4 rounded-md bg-slate-50 p-4 text-sm">
            <div className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5">
              {cfg.nextRunAt && (
                <>
                  <span className="text-slate-500">Nächstes Backup:</span>
                  <span className="font-medium text-slate-800">
                    {formatDate(cfg.nextRunAt)}
                  </span>
                </>
              )}
              {cfg.lastRunAt && (
                <>
                  <span className="text-slate-500">Letzter Lauf:</span>
                  <span className="text-slate-700">{formatDate(cfg.lastRunAt)}</span>
                  <span className="text-slate-500">Status:</span>
                  <span><StatusBadge status={cfg.lastStatus} /></span>
                </>
              )}
              {!cfg.lastRunAt && (
                <>
                  <span className="text-slate-500">Letzter Lauf:</span>
                  <span className="text-slate-400 italic">Noch nie ausgeführt</span>
                </>
              )}
            </div>
            {cfg.lastStatus === 'error' && cfg.lastError && (
              <div className="mt-2 rounded border border-red-200 bg-red-50 p-2 font-mono text-xs text-red-700">
                {cfg.lastError}
              </div>
            )}
          </div>

          {/* Jetzt ausführen */}
          <button
            onClick={() => runNowMutation.mutate()}
            disabled={runNowMutation.isPending}
            className="flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
            </svg>
            {runNowMutation.isPending ? 'Backup läuft…' : 'Jetzt Backup erstellen'}
          </button>
        </>
      )}
    </section>
  );
}

// ─── Seite ──────────────────────────────────────────────────────────────────

export function BackupPage() {
  return (
    <div className="flex h-full flex-col bg-slate-50">
      <header className="flex h-14 shrink-0 items-center border-b border-slate-200 bg-white px-4 shadow-sm">
        <div className="flex items-center gap-2 sm:gap-4">
          <h1 className="text-base font-semibold text-slate-800 sm:text-lg">Backup &amp; Wiederherstellung</h1>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl space-y-4 p-4 sm:space-y-6 sm:p-6">
          <LocalBackupSection />
          <TreeSection />
          <HetznerSection />
          <AutoBackupSection />
        </div>
      </main>
    </div>
  );
}

export default BackupPage;
